import { Injectable } from "@angular/core";
import { MatSnackBar } from "@angular/material/snack-bar";
import {
  Actions,
  createEffect,
  ofType,
  ROOT_EFFECTS_INIT,
} from "@ngrx/effects";
import { select, Store } from "@ngrx/store";
import { of, from, EMPTY } from "rxjs";
import {
  catchError,
  filter,
  map,
  switchMap,
  tap,
  withLatestFrom,
} from "rxjs/operators";

import { MessagingService } from "../../services";
import * as fromActions from "../actions";
import { getCurrentUser } from "../selectors";
import { IRootState } from "./../reducers";

@Injectable()
export class NotificationEffects {
  constructor(
    private actions$: Actions,
    private messaging: MessagingService,
    private snackBar: MatSnackBar,
    private store: Store<IRootState>
  ) {}

  init$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ROOT_EFFECTS_INIT),
      map(() => fromActions.loadFirebaseToken())
    )
  );

  loadFirebaseToken$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.loadFirebaseToken),
      switchMap(() =>
        from(this.messaging.initialize()).pipe(
          switchMap(() => from(this.messaging.requestToken())),
          map((token) =>
            fromActions.loadFirebaseTokenSuccess({ payload: token })
          ),
          catchError((error) => {
            // Check if this is a permission-related error
            const isPermissionError = 
              error?.code?.includes("permission") || 
              error?.code === "messaging/permission-blocked" ||
              error?.code === "messaging/permission-denied" ||
              error?.code === "messaging/permission-default";

            // Provide more detailed error information
            const errorMessage = 
              error?.message || 
              (typeof error === "string" ? error : "Unknown error occurred");
            
            // Log all errors for debugging
            if (isPermissionError) {
              console.warn("Firebase token request skipped - notification permission not granted:", errorMessage);
            } else {
              console.error("Firebase token request failed:", error);
            }

            // Only dispatch failure action for non-permission errors
            // Permission errors are user choices and shouldn't show error messages
            if (isPermissionError) {
              // Return EMPTY to silently complete without dispatching failure
              return EMPTY;
            }

            return of(
              fromActions.loadFirebaseTokenFailure({
                payload: {
                  message: errorMessage,
                  errors: error?.errors || {},
                },
              })
            );
          })
        )
      )
    )
  );
  

  loadFirebaseTokenSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(fromActions.loadFirebaseTokenSuccess),
      withLatestFrom(this.store.pipe(select(getCurrentUser))),
      filter(([{ payload }, user]) => !!payload && !!user),
      map(([{ payload }, user]) =>
        fromActions.updateUser({
          payload: { firebaseToken: payload, deviceId: "web" },
        })
      )
    );
  });

  loadFirebaseTokenFailure$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(fromActions.loadFirebaseTokenFailure),
        tap(({ payload }) =>
          console.warn('Firebase token request failed:', payload.message)
        )
      );
    },
    { dispatch: false }
  );
}
